import { Component, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { filter } from 'rxjs';
import { CodaltComponent } from 'src/app/codalt.component';
import { AddRealisationDialogComponent } from '../../check-hours/add-realisation-dialog/add-realisation-dialog.component';
import { DayReportStateService } from '../day-report-state.service';

@Component({
    selector: 'app-day-report-hour-control',
    standalone: false,
    templateUrl: './day-report-hour-control.component.html',
    styleUrl: './day-report-hour-control.component.scss',
})
export class DayReportHourControlComponent extends CodaltComponent {
    private stateService = inject(DayReportStateService);
    private dialog = inject(MatDialog);
    loadingData = toSignal(this.stateService.loadingData$);
    planning = toSignal(this.stateService.planning$);
    realisations = toSignal(this.stateService.realisations$.pipe(filter(v => !!v)));

    /**
     * Opens the shared add-realisation dialog from the day-report context.
     * Day report does not always have a normal AFAS project number, so we may
     * need to pass a fallback planning option to keep the project step usable.
     */
    public add() {
        const bookdate = this.planning()?.mainPlanning?.begindate;
        const afasProjectId = this.planning()?.afas_project_id ?? this.planning()?.worknumber;

        if (!this.planning()?.id || !bookdate) {
            return;
        }

        // Day report always knows which planning we are working on, so pass exactly
        // one project/planning option to the shared dialog and let it auto-select it.
        const projects = [
            {
                projectId: afasProjectId,
                planningId: this.planning().id,
                location: this.planning().location,
                begindate: this.planning().mainPlanning?.begindate,
                enddate: this.planning().mainPlanning?.enddate,
            },
        ];

        const userRealisations = (this.realisations() ?? []).filter(
            realisation => realisation.planning_id === this.planning().id && realisation.user_id
        );
        const activeUserIds = userRealisations
            .filter(realisation => !realisation.removed)
            .map(realisation => realisation.user_id);

        const ref = this.dialog.open(AddRealisationDialogComponent, {
            maxWidth: '550px',
            width: '100vw',
            maxHeight: '100%',
            disableClose: false,
            panelClass: 'comment-edit-dialog',
            data: {
                realisations: this.realisations() ?? [],
                bookdate,
                useOnlyProvidedProject: true,
                projects,
                plannedUserIds: [
                    ...new Set([
                        ...(this.planning().user_planning ?? []).map(up => up.user_id),
                        ...(this.planning().planning_has ?? []).map(ph => ph.driver_user_id),
                        // Employees added later in the day report have no user_planning, only registrations
                        ...activeUserIds,
                    ]),
                ].filter(
                    // Planned employees whose registrations were all removed were swapped to another crew
                    userId =>
                        userId &&
                        (activeUserIds.includes(userId) ||
                            !userRealisations.some(realisation => realisation.user_id === userId))
                ),
            },
        });

        this.subscriptions.add(
            ref.afterClosed().subscribe(() => {
                this.subscriptions.add(this.stateService.loadByPlanningId(this.planning().id));
            })
        );
    }
}
